/**
 * Marketplace Offer Engine (P1)
 *
 * Pure functions over `MarketplaceOffer[]`. The engine enforces the offer
 * lifecycle invariants — including the race-on-accept rule — and returns
 * BOTH the next offers array AND a list of canonical SystemEvents to emit.
 *
 * Callers (UI + dispatchers) drive state transitions ONLY through this
 * engine. Direct mutations of offer.status are forbidden.
 *
 * Lifecycle:
 *   pending → accepted   (sole acceptance; supersedes peer offers on same job)
 *   pending → declined   (contractor declined)
 *   pending → expired    (resolveOfferStatus, system-driven, time-based)
 *   pending → withdrawn  (dispatcher recall)
 *   pending → superseded (another offer on the same job won acceptance)
 *   accepted → superseded (replacement installer accepted later)
 *
 * Race-on-accept:
 *   acceptOffer() rejects when the target is no longer pending. Losing
 *   accepts return `{ ok: false, reason: "not_pending" }` deterministically —
 *   never throw. UI must surface this as a user-visible message, not a
 *   swallowed error.
 *
 * Eligibility is enforced at offer CREATION via assertEligible(), called
 * inside createOffer(). Action-level guard, not UI-level.
 */

import type { Contractor, Job } from "@/data/types";
import type { ContractorPayProfile } from "@/data/contractorPayProfile";
import type { ComplianceDocument } from "@/data/contractor/compliance/complianceDocuments";
import type { OnboardingSession } from "@/data/contractor/onboarding/types";
import { createSystemEvent } from "@/data/eventFactory";
import type { SystemEvent } from "@/data/communicationEngine";
import {
  assertEligible,
  type ContractorEligibility,
} from "./marketplaceEligibility";
import {
  DEFAULT_OFFER_TTL_MINUTES,
  generateOfferId,
  type MarketplaceOffer,
  type MarketplaceOfferStatus,
  type OfferAuditEntry,
  type OfferAuditEvent,
  type OfferDeclineReason,
} from "./marketplaceOffer";

// ── Helpers ──────────────────────────────────────────────────────────

function nowIso(): string {
  return new Date().toISOString();
}

function appendAudit(
  offer: MarketplaceOffer,
  event: OfferAuditEvent,
  statusAfter: MarketplaceOfferStatus,
  actor: string,
  reason?: string,
  at: string = nowIso(),
): MarketplaceOffer {
  const entry: OfferAuditEntry = { event, at, actor, reason, statusAfter };
  return { ...offer, audit: [...offer.audit, entry] };
}

function buildEvent(
  type:
    | "offer_created"
    | "offer_accepted"
    | "offer_declined"
    | "offer_expired"
    | "offer_withdrawn"
    | "offer_superseded"
    | "offer_extended"
    | "offer_window_changed",
  offer: MarketplaceOffer,
  actor: string,
  extra: Record<string, unknown> = {},
): SystemEvent {
  return createSystemEvent({
    type,
    jobId: offer.jobId,
    triggeredBy: actor,
    payload: {
      offerId: offer.id,
      contractorId: offer.contractorId,
      status: offer.status,
      ...extra,
    },
  });
}

// ── Result shapes ────────────────────────────────────────────────────

export interface OfferEngineSuccess {
  ok: true;
  offers: MarketplaceOffer[];
  events: SystemEvent[];
  /** The offer that was just acted on (post-state). */
  offer: MarketplaceOffer;
}
export interface OfferEngineFailure {
  ok: false;
  reason:
    | "ineligible"
    | "not_found"
    | "not_pending"
    | "duplicate_pending"
    | "already_assigned";
  /** Present when reason === "ineligible". */
  eligibility?: ContractorEligibility;
  failingFactors?: { key: string; reason: string }[];
}
export type OfferEngineResult = OfferEngineSuccess | OfferEngineFailure;

// ── Create ───────────────────────────────────────────────────────────

export interface CreateOfferInput {
  job: Job;
  contractor: Contractor;
  payProfiles: ContractorPayProfile[];
  sentBy: string;
  /** Optional override; defaults to DEFAULT_OFFER_TTL_MINUTES. */
  ttlMinutes?: number;
  /** DB-backed compliance documents — passed to assertEligible so the action
   * guard uses the same compliance data as the UI eligibility check. */
  complianceDocuments?: ComplianceDocument[];
  /** Onboarding sessions for agreement-signature evidence. */
  onboardingSessions?: OnboardingSession[];
  /** Parent contractor for inherited company-level requirements. */
  parentContractor?: Pick<Contractor, "id" | "entityType">;
  /**
   * Phase 1 — Offer Compensation:
   * Optional per-offer compensation override. When provided, the offer's
   * frozen `payoutAmountSnapshot` is set to this value instead of
   * `job.payoutAmount`. Does NOT mutate the job. Snapshot remains
   * immutable runtime truth.
   */
  payoutAmountOverride?: number;
  /**
   * Explicitly allow creation of a pending replacement offer for a job that
   * already has an accepted installer. Assignment still remains unchanged
   * until the replacement offer is accepted.
   */
  allowAcceptedReassignment?: boolean;
}

export function createOffer(
  offers: MarketplaceOffer[],
  input: CreateOfferInput,
): OfferEngineResult {
  // Hard rule: never offer a job that's already accepted unless the caller is
  // intentionally starting an accepted-installer reassignment cycle.
  const accepted = offers.find(
    (o) => o.jobId === input.job.id && o.status === "accepted",
  );
  if (accepted && !input.allowAcceptedReassignment) {
    return { ok: false, reason: "already_assigned" };
  }
  if (accepted && accepted.contractorId === input.contractor.id) {
    return { ok: false, reason: "already_assigned" };
  }

  // No duplicate pending offer to the same contractor for the same job.
  const dup = offers.find(
    (o) =>
      o.jobId === input.job.id &&
      o.contractorId === input.contractor.id &&
      o.status === "pending",
  );
  if (dup) return { ok: false, reason: "duplicate_pending" };

  // Action-level eligibility guard — pass the same compliance context the UI
  // used so both checks evaluate against identical data.
  const guard = assertEligible(input.contractor, {
    payProfiles: input.payProfiles,
    job: input.job,
    complianceDocuments: input.complianceDocuments,
    onboardingSessions: input.onboardingSessions,
    parentContractor: input.parentContractor,
  });
  if (!guard.ok) {
    return {
      ok: false,
      reason: "ineligible",
      eligibility: guard.eligibility,
      failingFactors: guard.failingFactors.map((f) => ({
        key: f.key,
        reason: f.reason,
      })),
    };
  }

  const sentAt = nowIso();
  const ttl = input.ttlMinutes ?? DEFAULT_OFFER_TTL_MINUTES;
  const expiresAt = new Date(Date.now() + ttl * 60_000).toISOString();

  // Phase 1: snapshot resolves override ?? job.payoutAmount. Snapshot is
  // immutable runtime truth for installer-facing surfaces and payout
  // lifecycle (installer_payouts). The job's payoutAmount is unchanged.
  const snapshot =
    typeof input.payoutAmountOverride === "number" &&
    Number.isFinite(input.payoutAmountOverride) &&
    input.payoutAmountOverride >= 0
      ? Math.round(input.payoutAmountOverride)
      : input.job.payoutAmount;

  let offer: MarketplaceOffer = {
    id: generateOfferId(),
    jobId: input.job.id,
    organizationId: input.job.organizationId ?? undefined,
    contractorId: input.contractor.id,
    status: "pending",
    sentAt,
    sentBy: input.sentBy,
    expiresAt,
    payoutAmountSnapshot: snapshot,
    audit: [],
  };
  offer = appendAudit(offer, "offer_created", "pending", input.sentBy);

  return {
    ok: true,
    offers: [...offers, offer],
    events: [
      buildEvent("offer_created", offer, input.sentBy, {
        expiresAt,
        payoutAmountSnapshot: snapshot,
        payoutOverrideApplied: snapshot !== input.job.payoutAmount,
      }),
    ],
    offer,
  };
}

// ── Accept (race-on-accept enforced) ─────────────────────────────────

export function acceptOffer(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string,
  at: string = nowIso(),
): OfferEngineResult {
  // Boundary expiry: resolve stale pending offers FIRST. Prevents accepting
  // a TTL-expired offer just because the periodic sweep hasn't run yet.
  const swept = resolveOfferStatus(offers, new Date(at));
  const target = swept.offers.find((o) => o.id === offerId);
  if (!target) return { ok: false, reason: "not_found" };
  if (target.status !== "pending") {
    return { ok: false, reason: "not_pending" };
  }

  let acceptedOffer: MarketplaceOffer = {
    ...target,
    status: "accepted",
    respondedAt: at,
  };
  acceptedOffer = appendAudit(acceptedOffer, "offer_accepted", "accepted", actor, undefined, at);

  const events: SystemEvent[] = [
    ...swept.events,
    buildEvent("offer_accepted", acceptedOffer, actor),
  ];

  // Supersede every other pending or previously accepted offer on the same
  // job. This preserves a single current accepted winner while allowing a
  // replacement offer cycle to stay pending without prematurely reassigning
  // the job.
  const next = swept.offers.map((o) => {
    if (o.id === offerId) return acceptedOffer;
    if (
      o.jobId === target.jobId &&
      (o.status === "pending" || o.status === "accepted")
    ) {
      const superseded: MarketplaceOffer = {
        ...o,
        status: "superseded",
        respondedAt: o.status === "pending" ? at : o.respondedAt,
      };
      const withAudit = appendAudit(
        superseded,
        "offer_superseded",
        "superseded",
        "system",
        o.status === "accepted"
          ? `Replacement offer ${offerId} accepted`
          : `Offer ${offerId} accepted`,
        at,
      );
      events.push(
        buildEvent("offer_superseded", withAudit, "system", {
          winningOfferId: offerId,
          supersededStatus: o.status,
        }),
      );
      return withAudit;
    }
    return o;
  });

  return { ok: true, offers: next, events, offer: acceptedOffer };
}

// ── Decline / Withdraw ───────────────────────────────────────────────

function transitionTerminal(
  offers: MarketplaceOffer[],
  offerId: string,
  status: Extract<MarketplaceOfferStatus, "declined" | "withdrawn" | "expired">,
  event: Extract<OfferAuditEvent, "offer_declined" | "offer_withdrawn" | "offer_expired">,
  actor: string,
  opts: { reason?: OfferDeclineReason; note?: string } = {},
  at: string = nowIso(),
): { result: OfferEngineResult; sweepEvents: SystemEvent[] } {
  // Boundary expiry: resolve any stale pending offer FIRST so we don't
  // act on an offer that should already be expired.
  const swept = resolveOfferStatus(offers, new Date(at));
  const target = swept.offers.find((o) => o.id === offerId);
  if (!target) {
    return { result: { ok: false, reason: "not_found" }, sweepEvents: swept.events };
  }
  if (target.status !== "pending") {
    return { result: { ok: false, reason: "not_pending" }, sweepEvents: swept.events };
  }

  let updated: MarketplaceOffer = {
    ...target,
    status,
    respondedAt: at,
    responseReason: opts.reason,
    responseNote: opts.note,
  };
  const auditMsg = [opts.reason, opts.note].filter(Boolean).join(" — ");
  updated = appendAudit(updated, event, status, actor, auditMsg || undefined, at);

  return {
    result: {
      ok: true,
      offers: swept.offers.map((o) => (o.id === offerId ? updated : o)),
      events: [
        ...swept.events,
        buildEvent(event, updated, actor, {
          ...(opts.reason ? { reason: opts.reason } : {}),
          ...(opts.note ? { note: opts.note } : {}),
        }),
      ],
      offer: updated,
    },
    sweepEvents: swept.events,
  };
}

export function declineOffer(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string,
  reason: OfferDeclineReason,
  note?: string,
): OfferEngineResult {
  return transitionTerminal(offers, offerId, "declined", "offer_declined", actor, {
    reason,
    note,
  }).result;
}

export function withdrawOffer(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string,
  note?: string,
): OfferEngineResult {
  return transitionTerminal(offers, offerId, "withdrawn", "offer_withdrawn", actor, { note })
    .result;
}

function transitionOfferWindow(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string,
  ttlMinutes: number,
  event: Extract<OfferAuditEvent, "offer_extended" | "offer_window_changed">,
  mode: "extend" | "change_window",
  at: string = nowIso(),
): OfferEngineResult {
  const swept = resolveOfferStatus(offers, new Date(at));
  const target = swept.offers.find((o) => o.id === offerId);
  if (!target) return { ok: false, reason: "not_found" };
  if (target.status !== "pending" && target.status !== "expired") {
    return { ok: false, reason: "not_pending" };
  }

  const ttlMs = ttlMinutes * 60_000;
  const nowMs = new Date(at).getTime();
  const currentExpiryMs = new Date(target.expiresAt).getTime();
  const sentAtMs = new Date(target.sentAt).getTime();
  const baseMs =
    mode === "extend"
      ? Math.max(Number.isFinite(currentExpiryMs) ? currentExpiryMs : nowMs, nowMs)
      : (Number.isFinite(sentAtMs) ? sentAtMs : nowMs);
  const nextExpiresAt = new Date(baseMs + ttlMs).toISOString();
  const nextStatus: MarketplaceOfferStatus =
    new Date(nextExpiresAt).getTime() > nowMs ? "pending" : "expired";

  let updated: MarketplaceOffer = {
    ...target,
    status: nextStatus,
    expiresAt: nextExpiresAt,
    respondedAt: nextStatus === "pending" ? undefined : at,
    responseReason: undefined,
    responseNote: undefined,
  };
  updated = appendAudit(
    updated,
    event,
    nextStatus,
    actor,
    `${mode === "extend" ? "Extended by" : "Changed to"} ${ttlMinutes} minutes`,
    at,
  );

  return {
    ok: true,
    offers: swept.offers.map((offer) => (offer.id === offerId ? updated : offer)),
    events: [
      ...swept.events,
      buildEvent(event, updated, actor, {
        ttlMinutes,
        expiresAt: nextExpiresAt,
        mode,
      }),
    ],
    offer: updated,
  };
}

export function extendOffer(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string,
  ttlMinutes: number,
  at: string = nowIso(),
): OfferEngineResult {
  return transitionOfferWindow(
    offers,
    offerId,
    actor,
    ttlMinutes,
    "offer_extended",
    "extend",
    at,
  );
}

export function changeOfferWindow(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string,
  ttlMinutes: number,
  at: string = nowIso(),
): OfferEngineResult {
  return transitionOfferWindow(
    offers,
    offerId,
    actor,
    ttlMinutes,
    "offer_window_changed",
    "change_window",
    at,
  );
}

export function expireOffer(
  offers: MarketplaceOffer[],
  offerId: string,
  actor: string = "system",
  at: string = nowIso(),
): OfferEngineResult {
  const swept = resolveOfferStatus(offers, new Date(at));
  const target = swept.offers.find((o) => o.id === offerId);
  if (!target) return { ok: false, reason: "not_found" };
  if (target.status === "expired") {
    return { ok: true, offers: swept.offers, events: swept.events, offer: target };
  }
  if (target.status !== "pending") {
    return { ok: false, reason: "not_pending" };
  }
  return transitionTerminal(
    swept.offers,
    offerId,
    "expired",
    "offer_expired",
    actor,
    {},
    at,
  ).result;
}

// ── Event-driven expiry ──────────────────────────────────────────────

/**
 * resolveOfferStatus — sweeps all pending offers and transitions any whose
 * `expiresAt` has passed into `expired`. Emits one `offer_expired` SystemEvent
 * per transition and appends to the audit trail.
 *
 * This is the ONLY path that may set an offer to `expired`. Read-derived
 * "looks-expired" states are forbidden — UI must read offer.status directly.
 *
 * Idempotent: re-running with the same `now` produces no new events.
 */
export function resolveOfferStatus(
  offers: MarketplaceOffer[],
  now: Date = new Date(),
): { offers: MarketplaceOffer[]; events: SystemEvent[] } {
  const nowMs = now.getTime();
  const events: SystemEvent[] = [];
  let mutated = false;
  const next = offers.map((o) => {
    if (o.status !== "pending") return o;
    if (new Date(o.expiresAt).getTime() > nowMs) return o;
    mutated = true;
    const at = now.toISOString();
    const expired: MarketplaceOffer = {
      ...o,
      status: "expired",
      respondedAt: at,
    };
    const withAudit = appendAudit(expired, "offer_expired", "expired", "system", "TTL elapsed", at);
    events.push(buildEvent("offer_expired", withAudit, "system", { expiredAt: at }));
    return withAudit;
  });
  return { offers: mutated ? next : offers, events };
}

// ── Derivations ──────────────────────────────────────────────────────

/**
 * "Awaiting Contractor Acceptance" — derived, not a stored job status.
 * True when the job has at least one PENDING offer and no ACCEPTED offer.
 */
export function isAwaitingContractorAcceptance(
  jobId: string,
  offers: MarketplaceOffer[],
): boolean {
  let hasPending = false;
  for (const o of offers) {
    if (o.jobId !== jobId) continue;
    if (o.status === "accepted") return false;
    if (o.status === "pending") hasPending = true;
  }
  return hasPending;
}

export function getActiveOfferForJob(
  jobId: string,
  offers: MarketplaceOffer[],
): MarketplaceOffer | null {
  return (
    offers.find((o) => o.jobId === jobId && o.status === "accepted") ??
    offers.find((o) => o.jobId === jobId && o.status === "pending") ??
    null
  );
}

export function getOffersForJob(
  jobId: string,
  offers: MarketplaceOffer[],
): MarketplaceOffer[] {
  return offers.filter((o) => o.jobId === jobId);
}

export function getOffersForContractor(
  contractorId: string,
  offers: MarketplaceOffer[],
): MarketplaceOffer[] {
  return offers.filter((o) => o.contractorId === contractorId);
}
